import { useEffect, useMemo, useRef, useState } from 'react'
import { analytics } from './analytics/ConsoleAnalyticsAdapter'
import { CandidateList } from './components/CandidateList'
import { DebugPanel } from './components/DebugPanel'
import { KRideReturnSurvey, type KRideSurveyAnswer } from './components/KRideReturnSurvey'
import { LanguageSelector } from './components/LanguageSelector'
import { ProviderCompare } from './components/ProviderCompare'
import { QuickDestinations } from './components/QuickDestinations'
import { RouteSummary } from './components/RouteSummary'
import { SearchPanel } from './components/SearchPanel'
import { quickDestinations } from './config/quickDestinations'
import { voiceDiagnostics } from './debug/VoiceDiagnosticStore'
import { providerConfig } from './config/providers'
import { BusanTaxiFareCalculator } from './fares/BusanTaxiFareCalculator'
import type { FareEstimate } from './fares/FareCalculator'
import { createTranslator, directionForLanguage, getBrowserPreferredLanguages, resolveLanguagePreference } from './i18n/i18n'
import { KRideProvider } from './mobility/KRideProvider'
import { MobilityRouter } from './mobility/MobilityRouter'
import { UberProvider } from './mobility/UberProvider'
import { UberDeepLinkError } from './mobility/uberDeepLink'
import { DemoPlacesProvider } from './places/DemoPlacesProvider'
import { FallbackPlaceProvider } from './places/FallbackPlaceProvider'
import { GooglePlacesProvider } from './places/GooglePlacesProvider'
import {
  estimatedFareBand,
  providerClickContext,
  type ProviderAnalyticsContext,
  type ProviderClickAnalyticsContext,
} from './poc/providerAnalytics'
import { providerReadiness } from './poc/providerReadiness'
import {
  getOrCreateProviderExperiment,
  providerOrderForVariant,
  providerPositionsForVariant,
  type StorageLike,
} from './poc/providerExperiment'
import {
  clearPendingTransition,
  createPendingTransition,
  readPendingTransition,
  writePendingTransition,
  type PendingProviderTransition,
} from './poc/providerTransition'
import { GoogleRoutesProvider } from './routes/GoogleRoutesProvider'
import type { Coordinates, Destination, Language, MobilityProviderId, QuickDestination, RouteInfo } from './types'
import { GeolocationRequestError, requestCurrentLocation } from './utils/geolocation'
import { BrowserVoiceInputProvider } from './voice/BrowserVoiceInputProvider'
import { FallbackVoiceInputProvider } from './voice/FallbackVoiceInputProvider'
import { VoiceInputError } from './voice/VoiceInputProvider'
import { WhisperVoiceInputProvider } from './voice/WhisperVoiceInputProvider'

const placeProvider = new FallbackPlaceProvider(new GooglePlacesProvider(), new DemoPlacesProvider())
const routeProvider = new GoogleRoutesProvider()
const fareCalculator = new BusanTaxiFareCalculator()
const krideProvider = new KRideProvider(providerConfig.mobility.kride)
const uberProvider = new UberProvider()
const mobilityRouter = new MobilityRouter(krideProvider, uberProvider)
const voiceDebugEnabled = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('debug') === '1'
const browserVoiceProvider = new BrowserVoiceInputProvider()
const paidVoiceFallbackProvider = new WhisperVoiceInputProvider({
  batchId: import.meta.env.VITE_STT_BATCH_ID,
  debug: voiceDebugEnabled,
  onDiagnostic: (event) => voiceDiagnostics.record(event),
})
const voiceProvider = new FallbackVoiceInputProvider(
  [
    // Use the browser's free speech recognition whenever it is available.
    // FallbackVoiceInputProvider advances only on UNSUPPORTED, so a normal
    // recognition failure never triggers a second recording or a paid API call.
    { id: 'web_speech', provider: browserVoiceProvider },
    { id: 'whisper', provider: paidVoiceFallbackProvider },
  ],
  ({ provider, outcome, durationMs, errorCode }) => {
    analytics.track('VOICE_PROVIDER_ATTEMPT', {
      voiceProvider: provider,
      outcome,
      durationMs,
      ...(errorCode ? { errorCode } : {}),
    })
  },
)
const LANGUAGE_STORAGE_KEY = 'kvibe_language'

function safeSessionStorage(): StorageLike | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function transitionMetadata(transition: PendingProviderTransition): ProviderClickAnalyticsContext {
  return {
    sessionId: transition.sessionId,
    language: transition.language,
    destinationId: transition.destinationId,
    provider: transition.provider,
    providerPosition: transition.providerPosition,
    providerVariant: transition.providerVariant,
    estimatedFareBand: transition.estimatedFareBand,
    krideEnabled: transition.krideEnabled,
    uberEnabled: transition.uberEnabled,
    locationAvailable: transition.locationAvailable,
  }
}

export default function App() {
  const initialLanguage = useMemo(() => {
    let storedLanguage: string | null = null
    try {
      storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    } catch {
      // Storage can be unavailable in private/restricted browser modes.
    }
    return resolveLanguagePreference({
      urlLanguage: new URLSearchParams(window.location.search).get('lang'),
      storedLanguage,
      preferredLanguages: getBrowserPreferredLanguages(),
    })
  }, [])
  const providerExperiment = useMemo(() => getOrCreateProviderExperiment(safeSessionStorage()), [])
  const providerOrder = useMemo(
    () => providerOrderForVariant(providerExperiment.providerVariant),
    [providerExperiment.providerVariant],
  )
  const providerPositions = useMemo(
    () => providerPositionsForVariant(providerExperiment.providerVariant),
    [providerExperiment.providerVariant],
  )
  const [language, setLanguage] = useState<Language>(initialLanguage.selectedLanguage)
  const t = useMemo(() => createTranslator(language), [language])
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState<Destination[]>([])
  const [selected, setSelected] = useState<Destination | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [location, setLocation] = useState<Coordinates | null>(null)
  const [locationState, setLocationState] = useState<'idle' | 'loading' | 'granted' | 'denied'>('idle')
  const [route, setRoute] = useState<RouteInfo | null>(null)
  const [fare, setFare] = useState<FareEstimate | null>(null)
  const [routeMessage, setRouteMessage] = useState<string | null>(null)
  const [routeLoading, setRouteLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [listening, setListening] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showKRideSurvey, setShowKRideSurvey] = useState(false)
  const [surveyThanks, setSurveyThanks] = useState(false)
  const [returnedKRideContext, setReturnedKRideContext] = useState<ProviderClickAnalyticsContext | null>(null)
  const [lastProviderClick, setLastProviderClick] = useState<MobilityProviderId | null>(null)
  const [debugOpen, setDebugOpen] = useState(voiceDebugEnabled)
  const destinationInputRef = useRef<HTMLInputElement>(null)
  const initialized = useRef(false)
  const providerCompareViewKey = useRef<string | null>(null)
  const providerCompareViewed = useRef(false)
  const providerSelected = useRef(false)
  const noSelectionTracked = useRef(false)
  const compareContext = useRef<ProviderAnalyticsContext | null>(null)
  const pendingTransition = useRef<PendingProviderTransition | null>(null)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    analytics.track('PAGE_VIEW', {
      version: '0.1.0',
      sessionId: providerExperiment.sessionId,
    })
    analytics.track('LANG_DETECTED', {
      detectedLanguage: initialLanguage.detectedLanguage,
      selectedLanguage: initialLanguage.selectedLanguage,
      source: initialLanguage.source,
    })
  }, [initialLanguage, providerExperiment])

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = directionForLanguage(language)
  }, [language])

  useEffect(() => {
    const storage = safeSessionStorage()
    pendingTransition.current = readPendingTransition(storage)
    let returnTimer: number | null = null

    const recordHidden = (signal: 'visibilitychange' | 'pagehide' | 'blur') => {
      const current = pendingTransition.current ?? readPendingTransition(storage)
      if (!current || current.hiddenAt !== null) return
      const updated = { ...current, hiddenAt: Date.now() }
      pendingTransition.current = updated
      writePendingTransition(storage, updated)
      analytics.track('PROVIDER_PAGE_HIDDEN', { ...transitionMetadata(updated), signal })
    }

    const recordReturned = (signal: 'visibilitychange' | 'pageshow' | 'focus' | 'mount') => {
      const current = pendingTransition.current ?? readPendingTransition(storage)
      if (!current || current.hiddenAt === null) return
      const metadata = transitionMetadata(current)
      pendingTransition.current = null
      clearPendingTransition(storage)
      analytics.track('PROVIDER_PAGE_RETURNED', { ...metadata, signal })
      if (current.provider === 'kride' && providerConfig.features.enableKRidePocSurvey) {
        setReturnedKRideContext(metadata)
        setShowKRideSurvey(true)
        setSurveyThanks(false)
        analytics.track('KRIDE_RETURN_SURVEY_VIEW', { ...metadata })
      }
    }

    if (pendingTransition.current && pendingTransition.current.hiddenAt !== null) {
      returnTimer = window.setTimeout(() => recordReturned('mount'), 0)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') recordHidden('visibilitychange')
      if (document.visibilityState === 'visible') recordReturned('visibilitychange')
    }
    const onPageHide = () => {
      recordHidden('pagehide')
      trackNoSelectionExit('pagehide')
    }
    const onPageShow = () => recordReturned('pageshow')
    const onBlur = () => recordHidden('blur')
    const onFocus = () => recordReturned('focus')

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    return () => {
      if (returnTimer !== null) window.clearTimeout(returnTimer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  useEffect(() => {
    if (!selected || !location || selected.coordinateStatus !== 'PROVIDER_VERIFIED') {
      setRouteLoading(false)
      setRoute(null)
      setFare(null)
      setRouteMessage(!location ? t('gpsRoutePrompt') : t('verifiedDestinationPrompt'))
      return
    }

    let active = true
    setRouteLoading(true)
    setRouteMessage(null)
    routeProvider.getRoute(location, selected)
      .then((result) => {
        if (!active) return
        setRoute(result)
        const estimate = fareCalculator.estimate(result)
        setFare(estimate)
        analytics.track('FARE_ESTIMATE_VIEW', { available: Boolean(estimate), region: fareCalculator.region })
      })
      .catch(() => {
        if (!active) return
        setRoute(null)
        setFare(null)
        setRouteMessage(t('routeApiRequired'))
      })
      .finally(() => active && setRouteLoading(false))
    return () => { active = false }
  }, [location, selected, t])

  const krideResult = useMemo(() => {
    try {
      return { url: mobilityRouter.buildKRideUrl(), error: null }
    } catch {
      return { url: null, error: t('krideLaunchFailed') }
    }
  }, [t])

  const uberResult = useMemo(() => {
    if (!location || !selected) return { url: null, error: null }
    try {
      return { url: mobilityRouter.buildUberUrl({ pickup: location, destination: selected }), error: null }
    } catch (buildError) {
      const message = buildError instanceof UberDeepLinkError
        ? ({
            CLIENT_ID_REQUIRED: t('uberClientRequired'),
            UNVERIFIED_DESTINATION: t('verifiedDestinationOnly'),
            INVALID_PICKUP: t('invalidPickup'),
            INVALID_DESTINATION: t('invalidDestination'),
          })[buildError.code]
        : t('uberUrlFailed')
      return { url: null, error: message }
    }
  }, [location, selected, t])

  const comparisonReadiness = useMemo(() => providerReadiness({
    destinationVerified: selected?.coordinateStatus === 'PROVIDER_VERIFIED',
    locationAvailable: Boolean(location),
    krideEnabled: Boolean(krideResult.url),
    uberEnabled: Boolean(uberResult.url),
  }), [krideResult.url, location, selected?.coordinateStatus, uberResult.url])

  useEffect(() => {
    if (!confirmed || !selected || !comparisonReadiness.comparisonEnabled) {
      compareContext.current = null
      return
    }
    const context: ProviderAnalyticsContext = {
      sessionId: providerExperiment.sessionId,
      language,
      destinationId: selected.id,
      providerVariant: providerExperiment.providerVariant,
      estimatedFareBand: estimatedFareBand(fare),
      krideEnabled: comparisonReadiness.krideEnabled,
      uberEnabled: comparisonReadiness.uberEnabled,
      locationAvailable: comparisonReadiness.locationAvailable,
    }
    compareContext.current = context
    const viewKey = `${selected.id}:${providerExperiment.providerVariant}`
    if (providerCompareViewKey.current === viewKey) return
    providerCompareViewKey.current = viewKey
    providerCompareViewed.current = true
    providerSelected.current = false
    noSelectionTracked.current = false
    analytics.track('PROVIDER_COMPARE_VIEW', {
      ...context,
      kridePosition: providerPositions.kride,
      uberPosition: providerPositions.uber,
    })
  }, [comparisonReadiness, confirmed, fare, language, providerExperiment, providerPositions, selected])

  async function runSearch(searchQuery = query) {
    const value = searchQuery.trim()
    if (value.length < 2) return
    setSearching(true)
    setError(null)
    setNotice(null)
    setSelected(null)
    setConfirmed(false)
    analytics.track('TEXT_SEARCH', { queryLength: value.length })
    analytics.track('DESTINATION_SEARCH', { method: 'text' })
    try {
      const result = await placeProvider.search(value, language)
      setCandidates(result.candidates)
      setNotice(result.mode === 'live' ? t('liveSearchNotice') : t('demoModeNotice'))
      analytics.track('CANDIDATE_SHOWN', { count: result.candidates.length, mode: result.mode })
      if (result.candidates.length === 0) setError(t('noCandidates'))
    } catch {
      setCandidates([])
      setError(t('searchFailed'))
    } finally {
      setSearching(false)
    }
  }

  function pickCandidate(destination: Destination) {
    setSelected(destination)
    setCandidates([])
    setConfirmed(false)
    setError(null)
    analytics.track('CANDIDATE_PICKED', { provider: destination.provider, coordinateStatus: destination.coordinateStatus })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function pickQuickDestination(destination: QuickDestination) {
    const fullQuery = [destination.nameKo, destination.nameEn, destination.address].filter(Boolean).join(' ')
    setQuery(fullQuery)
    setNotice(destination.verificationNote)
    await runSearch(fullQuery)
  }

  async function requestGps() {
    setLocationState('loading')
    setError(null)
    analytics.track('GPS_REQUEST')
    try {
      const current = await requestCurrentLocation()
      setLocation(current)
      setLocationState('granted')
      analytics.track('GPS_GRANTED', { accuracyStored: false })
    } catch (gpsError) {
      setLocation(null)
      setLocationState('denied')
      setError(gpsError instanceof GeolocationRequestError
        ? ({ UNSUPPORTED: t('gpsUnsupported'), DENIED: t('gpsDenied'), UNAVAILABLE: t('gpsUnavailable') })[gpsError.code]
        : t('gpsUnavailable'))
      analytics.track('GPS_DENIED')
    }
  }

  async function startVoice() {
    setListening(true)
    setError(null)
    analytics.track('VOICE_START')
    try {
      const transcript = await voiceProvider.listen(language)
      if (!transcript) throw new Error('NO_TRANSCRIPT')
      setQuery(transcript)
      analytics.track('VOICE_SUCCESS', { transcriptLength: transcript.length })
      await runSearch(transcript)
    } catch (voiceError) {
      setError(voiceError instanceof VoiceInputError
        ? ({
            UNSUPPORTED: t('voiceUnavailable'),
            PERMISSION_DENIED: t('voiceRecognitionFailed'),
            RECORDING_FAILED: t('voiceRecognitionFailed'),
            NETWORK_FAILED: t('voiceRecognitionFailed'),
            TRANSCRIPTION_FAILED: t('voiceRecognitionFailed'),
            RECOGNITION_FAILED: t('voiceRecognitionFailed'),
            ENDED: t('voiceEnded'),
          })[voiceError.code]
        : voiceError instanceof Error && voiceError.message === 'NO_TRANSCRIPT'
          ? t('voiceNoTranscript')
          : t('voiceFailed'))
      analytics.track('VOICE_FAIL', {
        errorCode: voiceError instanceof VoiceInputError ? (voiceError.upstreamCode ?? voiceError.code) : 'UNKNOWN',
      })
      destinationInputRef.current?.focus()
    } finally {
      setListening(false)
    }
  }

  function confirmDestination() {
    setConfirmed(true)
    analytics.track('DESTINATION_CONFIRMED', { provider: selected?.provider ?? 'unknown' })
  }

  function trackNoSelectionExit(reason: 'pagehide' | 'destination_reset') {
    if (!providerCompareViewed.current || providerSelected.current || noSelectionTracked.current) return
    const context = compareContext.current
    if (!context) return
    noSelectionTracked.current = true
    analytics.track('PROVIDER_NO_SELECTION_EXIT', {
      ...context,
      reason,
      kridePosition: providerPositions.kride,
      uberPosition: providerPositions.uber,
    })
  }

  function currentProviderClickContext(provider: MobilityProviderId): ProviderClickAnalyticsContext | null {
    if (!selected || !comparisonReadiness.comparisonEnabled) return null
    const context: ProviderAnalyticsContext = {
      sessionId: providerExperiment.sessionId,
      language,
      destinationId: selected.id,
      providerVariant: providerExperiment.providerVariant,
      estimatedFareBand: estimatedFareBand(fare),
      krideEnabled: comparisonReadiness.krideEnabled,
      uberEnabled: comparisonReadiness.uberEnabled,
      locationAvailable: comparisonReadiness.locationAvailable,
    }
    return providerClickContext(context, provider, providerPositions[provider])
  }

  function markProviderClick(provider: MobilityProviderId): ProviderClickAnalyticsContext | null {
    const context = currentProviderClickContext(provider)
    if (!context) return null
    providerSelected.current = true
    setLastProviderClick(provider)
    const pending = createPendingTransition(context)
    pendingTransition.current = pending
    writePendingTransition(safeSessionStorage(), pending)
    if (provider === 'kride') {
      analytics.track('KRIDE_SELECTED', { ...context })
      analytics.track('KRIDE_CLICK', { ...context })
    } else {
      analytics.track('UBER_SELECTED', { ...context })
      analytics.track('UBER_CLICK', { ...context })
    }
    return context
  }

  function openKRide() {
    if (!selected || !comparisonReadiness.comparisonEnabled || !krideResult.url) {
      setError(uberResult.error ?? krideResult.error ?? t('providerUnavailable'))
      return
    }
    const context = markProviderClick('kride')
    if (!context) return
    try {
      mobilityRouter.openKRide()
    } catch {
      pendingTransition.current = null
      clearPendingTransition(safeSessionStorage())
      setError(t('krideLaunchFailed'))
      analytics.track('KRIDE_LINK_ERROR', { ...context, reason: 'official_app_start_url_failed' })
    }
  }

  function openUber() {
    if (!location || !selected) {
      const message = t('missingLocationDestination')
      setError(message)
      analytics.track('UBER_LINK_ERROR', { reason: 'missing_location_or_destination' })
      return
    }
    if (!comparisonReadiness.comparisonEnabled || !uberResult.url) {
      setError(uberResult.error ?? t('providerUnavailable'))
      return
    }
    const context = markProviderClick('uber')
    if (!context) return
    try {
      mobilityRouter.openUber({ pickup: location, destination: selected })
    } catch (linkError) {
      pendingTransition.current = null
      clearPendingTransition(safeSessionStorage())
      const message = linkError instanceof UberDeepLinkError
        ? ({
            CLIENT_ID_REQUIRED: t('uberClientRequired'),
            UNVERIFIED_DESTINATION: t('verifiedDestinationOnly'),
            INVALID_PICKUP: t('invalidPickup'),
            INVALID_DESTINATION: t('invalidDestination'),
          })[linkError.code]
        : t('uberLinkFailed')
      setError(message)
      analytics.track('UBER_LINK_ERROR', { ...context, reason: 'builder_error' })
    }
  }

  function selectProvider(provider: MobilityProviderId) {
    setError(null)
    if (!comparisonReadiness.comparisonEnabled) {
      setError(uberResult.error ?? krideResult.error ?? t('providerUnavailable'))
      return
    }
    if (provider === 'kride') openKRide()
    else openUber()
  }

  function answerKRideSurvey(answer: KRideSurveyAnswer) {
    const eventByAnswer = {
      opened: 'KRIDE_OPENED_SELF_REPORT',
      install: 'KRIDE_INSTALL_SCREEN_SELF_REPORT',
      failed: 'KRIDE_OPEN_FAILED_SELF_REPORT',
      unknown: 'KRIDE_UNKNOWN_SELF_REPORT',
    } as const
    analytics.track(eventByAnswer[answer], returnedKRideContext ? { ...returnedKRideContext } : {})
    setShowKRideSurvey(false)
    setSurveyThanks(true)
  }

  function resetDestination() {
    trackNoSelectionExit('destination_reset')
    setSelected(null)
    setConfirmed(false)
    setRoute(null)
    setFare(null)
    setNotice(null)
    setError(null)
    setShowKRideSurvey(false)
    setSurveyThanks(false)
    providerCompareViewKey.current = null
    providerCompareViewed.current = false
    providerSelected.current = false
    noSelectionTracked.current = false
    compareContext.current = null
  }

  function changeLanguage(next: Language) {
    setLanguage(next)
    setNotice(null)
    setError(null)
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next)
    } catch {
      // The selection still applies to the current session when storage is blocked.
    }
    analytics.track('LANG_OVERRIDE', {
      detectedLanguage: initialLanguage.detectedLanguage,
      selectedLanguage: next,
      source: 'manual',
    })
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label={t('appHomeLabel')}>
          <span className="brand-mark">K</span>
          <span><strong>K-VIBE</strong><small>Voice Mobility</small></span>
        </a>
        <div className="top-actions">
          <button className={`gps-chip ${locationState}`} type="button" onClick={requestGps} disabled={locationState === 'loading'}>
            <span aria-hidden="true">⌖</span>
            {locationState === 'granted' ? t('gpsGranted') : locationState === 'loading' ? t('gpsLoading') : t('gpsCurrent')}
          </button>
          <LanguageSelector language={language} t={t} onChange={changeLanguage} />
        </div>
      </header>

      <main>
        {!selected ? (
          <>
            <SearchPanel
              query={query}
              t={t}
              searching={searching}
              listening={listening}
              voiceAvailable={voiceProvider.available}
              inputRef={destinationInputRef}
              onQueryChange={setQuery}
              onSearch={() => runSearch()}
              onVoice={startVoice}
            />
            {notice && <div className="banner info" role="status">{notice}</div>}
            {error && (
              <div className="banner error banner-with-action" role="alert">
                <span>{error}</span>
                {query.trim().length >= 2 && (
                  <button className="retry-button" type="button" onClick={() => runSearch()} disabled={searching}>
                    {t('retry')}
                  </button>
                )}
              </div>
            )}
            <CandidateList candidates={candidates} t={t} onPick={pickCandidate} />
            <QuickDestinations destinations={quickDestinations} language={language} t={t} onPick={pickQuickDestination} />
          </>
        ) : (
          <section className="confirmation" aria-labelledby="confirmation-heading">
            <button className="back-button" type="button" onClick={resetDestination}><span className="back-arrow" aria-hidden="true">←</span> {t('backDestination')}</button>
            <div className="confirmation-card">
              <span className="step">2</span>
              <p className="eyebrow">{t('destinationEyebrow')}</p>
              <h1 id="confirmation-heading"><bdi>{selected.name}</bdi></h1>
              <p className="address"><bdi>{selected.address}</bdi></p>
              <p className={`status-badge ${selected.coordinateStatus.toLowerCase()}`}><bdi>{selected.coordinateStatus}</bdi></p>
              {selected.coordinateStatus === 'DEMO_ONLY' && <div className="banner info">{t('demoCoordinateWarning')}</div>}
              <RouteSummary route={route} fare={fare} loading={routeLoading} message={routeMessage} language={language} t={t} />
              {!confirmed ? (
                <button className="confirm-button" type="button" onClick={confirmDestination}>{t('confirmDestination')}</button>
              ) : (
                <>
                  {selected.coordinateStatus !== 'PROVIDER_VERIFIED' ? (
                    <div className="banner info">{t('providerUnavailable')}</div>
                  ) : !comparisonReadiness.locationAvailable ? (
                    <section className="provider-prerequisite" aria-label={t('allowLocation')}>
                      <p>{t('gpsRoutePrompt')}</p>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={requestGps}
                        disabled={locationState === 'loading'}
                      >
                        {locationState === 'loading' ? t('gpsLoading') : t('allowLocation')}
                      </button>
                    </section>
                  ) : !comparisonReadiness.comparisonEnabled ? (
                    <div className="banner error" role="alert">
                      {uberResult.error ?? krideResult.error ?? t('providerUnavailable')}
                    </div>
                  ) : (
                    <ProviderCompare
                      order={providerOrder}
                      t={t}
                      comparisonEnabled={comparisonReadiness.comparisonEnabled}
                      onSelect={selectProvider}
                    />
                  )}
                  {showKRideSurvey && <KRideReturnSurvey t={t} onAnswer={answerKRideSurvey} />}
                  {surveyThanks && <p className="survey-thanks" role="status">{t('krideSurveyThanks')}</p>}
                </>
              )}
              {error && <div className="banner error" role="alert">{error}</div>}
            </div>
          </section>
        )}
      </main>

      <footer>
        <p>{t('privacyFooter')}</p>
        <button type="button" className="debug-toggle" onClick={() => setDebugOpen((value) => !value)}>
          {debugOpen ? t('debugClose') : t('debugOpen')}
        </button>
      </footer>
      <DebugPanel
        open={debugOpen}
        language={language}
        pickup={location}
        destination={selected}
        fare={fare}
        providerVariant={providerExperiment.providerVariant}
        kridePosition={providerPositions.kride}
        uberPosition={providerPositions.uber}
        krideUrl={krideResult.url}
        kridePrefillSupported={krideProvider.supportsDestinationPrefill}
        uberUrl={uberResult.url}
        uberError={uberResult.error}
        krideEnabled={comparisonReadiness.krideEnabled}
        uberEnabled={comparisonReadiness.uberEnabled}
        locationAvailable={comparisonReadiness.locationAvailable}
        comparisonEnabled={comparisonReadiness.comparisonEnabled}
        lastProviderClick={lastProviderClick}
        voiceDebugEnabled={voiceDebugEnabled}
        t={t}
      />
    </div>
  )
}
